import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../../config/firebase";
import { Listbox } from "@headlessui/react";
import { useCart } from "../../../contexts/CartContext";
import { useNotification } from "../../../contexts/NotificationContext";
import { ChevronDown, Filter, X } from "lucide-react";

const gradientClass =
  "bg-gradient-to-b from-[#484139] via-[#544C44] via-[#5D554C] via-[#655E54] to-[#6B5B4F]";

const sortOptions = [
  { value: "", label: "Default" },
  { value: "a-z", label: "Name (A - Z)" },
  { value: "z-a", label: "Name (Z - A)" },
  { value: "price-low", label: "Price (Low → High)" },
  { value: "price-high", label: "Price (High → Low)" },
];

const filterCategories = [
  "Shampoo",
  "Conditioner",
  "Body Lotion",
  "Moisturizer",
  "Cleanser",
  "Serum",
  "Toner",
  "Spray Sunscreen",
  "Stick Sunscreen",
];

const topCategories = [
  { name: "Hair Care", key: "hair", icon: "/HairCareIcon.png" },
  { name: "Body Care", key: "body", icon: "/BodyCareIcon.png" },
  { name: "Facial Care", key: "face", icon: "/FacialCareIcon.png" },
  { name: "Sun Protection", key: "sun", icon: "/SunProtectionIcon.png" },
];

const sidebarKeywordMap = {
  Shampoo: ["shampoo"],
  Conditioner: ["conditioner"],
  "Body Lotion": ["body lotion"],
  Moisturizer: ["moisturizer"],
  Cleanser: ["cleanser"],
  Serum: ["serum"],
  Toner: ["toner"],
  "Spray Sunscreen": ["spray sunscreen"],
  "Stick Sunscreen": ["stick sunscreen"],
};

const AllProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [sortBy, setSortBy] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { addToCart } = useCart();
  const { showSuccess, showError } = useNotification();

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      addToCart(product);
      showSuccess(`${product.name} added to cart successfully!`);
    } catch (error) {
      console.error("Error adding to cart:", error);
      showError("Failed to add product to cart");
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsRef = collection(db, "products");
        const querySnapshot = await getDocs(productsRef);
        const productsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(productsData);
        setFilteredProducts(productsData);
      } catch (error) {
        console.error("Error fetching products:", error);
        setProducts([]);
        setFilteredProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    let filtered = [...products];

    if (searchTerm) {
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (product.description &&
            product.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (appliedFilters.length > 0) {
      filtered = filtered.filter((product) => {
        const searchable = `${product.name || ""} ${product.description || ""}`.toLowerCase();
        return appliedFilters.some((filterLabel) => {
          const keywords = sidebarKeywordMap[filterLabel] || [];
          return keywords.some((kw) => searchable.includes(kw));
        });
      });
    }

    if (activeCategory !== "All") {
      filtered = filtered.filter(
        (product) =>
          product.category === activeCategory || product.topCategory === activeCategory
      );
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "a-z":
          return a.name.localeCompare(b.name);
        case "z-a":
          return b.name.localeCompare(a.name);
        case "price-low":
          return a.price - b.price;
        case "price-high":
          return b.price - a.price;
        default:
          return 0;
      }
    });

    setFilteredProducts(filtered);
  }, [products, searchTerm, appliedFilters, sortBy, activeCategory]);

  if (loading) {
    return (
      <div className={`${gradientClass} min-h-screen`}>
        <div className="container mx-auto px-4 py-16 text-center text-[#E3D5C5]">
          Loading products...
        </div>
      </div>
    );
  }

  // Filter sidebar content (used both for desktop sidebar and mobile drawer)
  const FilterContent = () => (
    <div className="bg-[#D4B998] rounded-2xl p-5 sm:p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg sm:text-xl font-semibold text-[#463C30]">Filter</h3>
        <button
          onClick={() => setAppliedFilters([])}
          className="text-sm text-[#927b5e] hover:text-[#463c30]"
        >
          clear all
        </button>
      </div>

      <input
        type="text"
        placeholder="Search products..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full mb-5 px-3 py-2 border border-[#927b5e] rounded-lg text-sm bg-white"
      />

      <div className="mb-6">
        <h4 className="text-[#463c30] font-medium mb-3">Category</h4>
        <div className="space-y-2.5">
          {filterCategories.map((category) => (
            <div key={category} className="flex items-center gap-3">
              <input
                type="checkbox"
                id={category}
                checked={appliedFilters.includes(category)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setAppliedFilters([...appliedFilters, category]);
                  } else {
                    setAppliedFilters(appliedFilters.filter((c) => c !== category));
                  }
                }}
                className="accent-[#8f72d9] w-4 h-4"
              />
              <label htmlFor={category} className="text-[#463c30] text-sm cursor-pointer">
                {category}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h4 className="text-[#463c30] font-medium mb-3">Sort by</h4>
        <Listbox value={sortBy} onChange={setSortBy}>
          <div className="relative">
            <Listbox.Button className="w-full px-3 py-2 border border-[#927b5e] rounded-lg text-sm bg-white flex items-center justify-between">
              <span>{sortOptions.find((o) => o.value === sortBy)?.label || "Select"}</span>
              <ChevronDown className="w-4 h-4" />
            </Listbox.Button>
            <Listbox.Options className="absolute w-full mt-1 rounded-lg bg-white text-black shadow-lg z-10 overflow-hidden">
              {sortOptions.map((option) => (
                <Listbox.Option
                  key={option.value}
                  value={option.value}
                  className={({ active }) =>
                    `cursor-pointer select-none px-3 py-2 ${
                      active ? "bg-[#dec6a9]" : "bg-white"
                    }`
                  }
                >
                  {option.label}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </div>
        </Listbox>
      </div>
    </div>
  );

  return (
    <div className={`${gradientClass} min-h-screen`}>
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Hero */}
        <section className="mb-8 sm:mb-10">
          <div className="bg-[#D4B998] rounded-2xl sm:rounded-3xl px-5 sm:px-10 lg:px-12 py-10 sm:py-14 lg:py-16 text-center relative overflow-hidden">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#463c30] mb-4 sm:mb-6">
              Elevate Your Routine
            </h1>
            <p className="text-sm sm:text-base lg:text-lg text-[#463C30] max-w-4xl mx-auto leading-relaxed">
              Explore our carefully curated collection of beauty and wellness products
              designed to nourish, protect, and enhance. From skincare essentials to hair
              care and body care, find everything you need to elevate your routine.
            </p>
          </div>
        </section>

        {/* Top Category Bar */}
        <section className="mb-8 sm:mb-10">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            {topCategories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`flex items-center gap-3 rounded-2xl px-3 sm:px-5 py-3 sm:py-4 shadow-md transition-all duration-300 cursor-pointer hover:scale-[1.02] hover:shadow-xl ${
                  activeCategory === cat.key
                    ? "bg-[#3c352d] text-white shadow-2xl"
                    : "bg-[#D4B998] text-[#463C30]"
                }`}
              >
                <div className="w-10 h-10 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                  <img
                    src={cat.icon}
                    alt={cat.name}
                    className="w-full h-full object-contain p-1"
                  />
                </div>
                <span className="font-serif font-semibold text-xs sm:text-sm lg:text-base text-left">
                  {cat.name}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-4 sm:mt-6 flex justify-center lg:justify-end">
            <button
              onClick={() => {
                setActiveCategory("All");
                setAppliedFilters([]);
                setSearchTerm("");
              }}
              className="border-2 border-[#D4B998] text-[#E3D5C5] bg-transparent rounded-lg px-6 sm:px-8 py-2 font-semibold text-sm sm:text-base hover:bg-[#e1caa5] hover:text-[#3b332b] transition"
            >
              All Products
            </button>
          </div>
        </section>

        {/* Mobile filter button */}
        <div className="lg:hidden mb-5 flex justify-end">
          <button
            onClick={() => setIsFilterOpen(true)}
            className="inline-flex items-center gap-2 bg-[#D4B998] text-[#463C30] px-4 py-2 rounded-lg font-semibold text-sm shadow"
          >
            <Filter className="w-4 h-4" />
            Filter & Sort
          </button>
        </div>

        {/* Filter + Products grid */}
        <section className="pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
            {/* Desktop sidebar */}
            <aside className="hidden lg:block lg:col-span-1">
              <FilterContent />
            </aside>

            {/* Products */}
            <div className="lg:col-span-3">
              {filteredProducts.length === 0 ? (
                <p className="text-center text-white py-12">No products found.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                  {filteredProducts.map((product) => (
                    <div
                      key={product.id}
                      className="bg-white rounded-2xl shadow-sm group relative flex flex-col"
                    >
                      {product.stockQuantity &&
                        product.stockQuantity > 0 &&
                        product.stockQuantity < 10 && (
                          <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-semibold z-10">
                            Only {product.stockQuantity} left
                          </div>
                        )}

                      {(!product.stockQuantity || product.stockQuantity === 0) && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-2xl z-10">
                          <span className="text-white font-semibold">Out of Stock</span>
                        </div>
                      )}

                      <Link to={`/product/${product.id}`} className="flex flex-col flex-1">
                        <div className="aspect-square bg-gray-100 p-3 sm:p-4 overflow-hidden rounded-2xl">
                          <img
                            src={
                              (Array.isArray(product.images) &&
                                (typeof product.images[0] === "string"
                                  ? product.images[0]
                                  : product.images[0]?.url)) ||
                              "/placeholder-product.jpg"
                            }
                            alt={product.name}
                            loading="lazy"
                            className="w-full h-full object-cover transform transition-transform duration-300 group-hover:scale-105"
                          />
                        </div>
                        <div className="p-4 sm:p-5 flex flex-col flex-1">
                          <h3 className="text-[#463c30] font-medium text-base sm:text-lg mb-1 line-clamp-2">
                            {product.name}
                          </h3>
                          <p className="text-[#463c30] text-base sm:text-lg font-semibold mb-3">
                            LKR {product.price}
                          </p>
                          <button
                            onClick={(e) => handleAddToCart(product, e)}
                            disabled={!product.stockQuantity || product.stockQuantity === 0}
                            className="mt-auto w-full bg-[#d4b998] text-[#463c30] hover:bg-[#ddbb92] rounded-full font-medium py-2 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
                          >
                            {!product.stockQuantity || product.stockQuantity === 0
                              ? "Out of Stock"
                              : "Add to cart"}
                          </button>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Mobile filter drawer */}
      {isFilterOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setIsFilterOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-[#3c352d] p-4 overflow-y-auto animate-slide-in">
            <div className="flex items-center justify-between mb-4">
              <span className="font-serif text-lg text-[#E3D5C5]">Filter & Sort</span>
              <button
                onClick={() => setIsFilterOpen(false)}
                className="text-[#E3D5C5] p-1.5 rounded-lg hover:bg-[#1f1916]"
                aria-label="Close filter"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <FilterContent />
          </div>
        </div>
      )}
    </div>
  );
};

export default AllProducts;
